import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BonusModal, BonusText, GiverName, parseContent } from '@bonusly/common-bonus';
import { Bookmark } from '@bonusly/common-bookmark';
import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, ClappingHandsSolid, Flex, rem, Spacer, StyleProps, Text } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { ReferenceUnlinkButton } from '../references-carousel/reference-unlink-button';

export interface BonusCardProps extends StyleProps {
	bonus: Bonus;
	onUnlinkBonus?: (bonusId: string) => void;
	sectionTitle?: string;
}

export const BonusCard: FC<BonusCardProps> = ({ bonus, onUnlinkBonus, sectionTitle, ...rest }) => {
	const { t } = useTranslation(['one_on_ones', 'common']);
	const [modalOpen, setModalOpen] = useState(false);

	const { text } = useMemo(() => {
		return parseContent(bonus);
	}, [bonus]);

	if (!bonus) return null;

	const childBonuses = bonus.child_bonuses?.length || 0;
	const reactions = bonus.reactions?.length || 0;

	return (
		<Box
			bg="white"
			p={rem(16)}
			borderRadius="lg"
			cursor="pointer"
			onClick={(e) => {
				// Don't open modal if clicking on interactive elements
				const target = e.target as HTMLElement;
				if (
					target.closest('button') ||
					target.closest('a') ||
					target.closest('[role="button"]') ||
					target.closest('[role="menuitem"]') ||
					target.closest('[role="option"]') ||
					target.closest('video') ||
					target.closest('.video-controls')
				) {
					return;
				}
				setModalOpen(true);
			}}
			mx="auto" // Centers the box horizontally
			gap={2}
			sx={{ breakInside: 'avoid' }}
			{...rest}
		>
			{modalOpen && (
				<BonusModal
					bonus={bonus}
					commentsDefaultOpen={false}
					handleCloseModal={() => {
						setModalOpen(false);
					}}
					isLoading={false}
				/>
			)}
			<Bookmark key={bonus.id} referenceId={bonus.id} referenceType="bonus" referenceText={bonus.reason} bookmark={bonus.bookmark}>
				<Flex gap={2} alignItems="center" borderBottom="solid" paddingBottom={3} borderColor="border.soft">
					<Flex backgroundColor="success.bg" borderRadius="full" padding="4.8px">
						<ClappingHandsSolid color="success.icon" />
					</Flex>
					<Text size="lg" fontWeight="semibold">
						{t('meeting.carousel.recognition')}
					</Text>
					<Spacer />
					<RelativeTime color="gray.600" date={new Date(bonus.created_at)} fontSize={{ base: 'xs', xs: 'sm' }} />
					<Flex>
						{onUnlinkBonus && sectionTitle && <ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkBonus(bonus.id)} sectionTitle={sectionTitle} />}
					</Flex>
				</Flex>

				<BonusText bonus={bonus} mt={3} prefix={<GiverName bonus={bonus} />} text={text} noOfLines={4} />

				<Flex dir="row" alignItems="center" pt={rem(8)}>
					{childBonuses > 0 && (
						<Text color="gray.700" size="md">
							{t('meeting.bonus_footer.reply_count', {
								count: childBonuses,
							})}
						</Text>
					)}
					{childBonuses > 0 && reactions > 0 && <Box as="span" mx={1} borderRadius="full" w="2px" h="2px" bg="gray.700"></Box>}
					{reactions > 0 && (
						<Text color="gray.700" size="md">
							{t('meeting.bonus_footer.reaction_count', {
								count: reactions,
							})}
						</Text>
					)}
					<Spacer />
				</Flex>
			</Bookmark>
		</Box>
	);
};
